import { useState } from "react";
import { Link } from "wouter";
import { HelpCircle } from "lucide-react";
import { Trans, useT } from "@/lib/i18n";
import { Sheet } from "@/components/ux/Sheet";
import { useBookingContext } from "@/components/booking/getbookingContext";
import { BOOKING_CUTOFF_HOURS, CANCELLATION_REFUND_WINDOW_HOURS, NO_SHOW_GRACE_HOURS, noShowFeeFor } from "@shared/constants";
import { useLandingPricing } from "@/lib/api/useLandingPricing";
import { formatPrice } from "@/utils/priceUtils";

// The no-show fee disclaimer, shown above the booking CTA. Renders nothing
// unless the no_show_disclaimer experiment is on. Booking with this on screen
// is the agreement: the booking is stamped `noShowDisclaimerAccepted`, which is
// what makes it chargeable. "Learn more" opens a sheet with the cutoff times.

const CUTOFFS = ["change", "details", "cancel", "exception"] as const;

export function NoShowDisclaimer() {
  const { t } = useT();
  const { noShowDisclaimerShown } = useBookingContext();
  const [open, setOpen] = useState(false);
  // The fee in the visitor's currency, from the same public rate card the
  // onboarding price step uses. Until it loads, the USD table entry.
  const { data: pricing } = useLandingPricing();
  const feeAmount = pricing?.data?.noShowFee ?? noShowFeeFor(pricing?.data?.currency).amount;
  const feeCurrency = pricing?.data?.currency ?? noShowFeeFor(null).currency;
  if (!noShowDisclaimerShown) return null;
  const fee = formatPrice(feeAmount, feeCurrency);

  const vars = {
    fee,
    changeHours: CANCELLATION_REFUND_WINDOW_HOURS,
    detailsHours: 24,
    cancelHours: BOOKING_CUTOFF_HOURS,
    graceDays: Math.round(NO_SHOW_GRACE_HOURS / 24),
  };

  return (
    <>
      <div className="mt-5 flex items-center gap-3 px-1">
        <p className="flex-1 min-w-0 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
          <Trans
            i18nKey="booking.noShow.agree"
            values={vars}
            components={{
              terms: <Link href="/terms" className="underline underline-offset-2 hover:text-primary-500" />,
              b: <span className="font-semibold text-neutral-800 dark:text-neutral-200" />,
            }}
          />
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("booking.noShow.learnMore")}
          title={t("booking.noShow.learnMore")}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-500/15 text-primary-500 flex items-center justify-center hover:bg-primary-500/25 transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <Sheet open={open} onClose={() => setOpen(false)} hideHeader className="sm:max-w-sm">
        <div className="space-y-5">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            {t("booking.noShow.sheet.title")}
          </h3>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 items-baseline">
            {CUTOFFS.map((key) => (
              <div key={key} className="contents">
                <dt className="text-xs font-semibold uppercase tracking-wide text-primary-500 whitespace-nowrap">
                  {t(`booking.noShow.sheet.cutoffs.${key}.when`, vars)}
                </dt>
                <dd className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">
                  {t(`booking.noShow.sheet.cutoffs.${key}.what`, vars)}
                </dd>
              </div>
            ))}
          </dl>

          <div className="rounded-xl bg-neutral-100 dark:bg-neutral-800 p-4">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {t("booking.noShow.sheet.whyTitle")}
            </p>
            <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              {t("booking.noShow.sheet.whyBody", vars)}
            </p>
          </div>

          <button type="button" onClick={() => setOpen(false)} className="btn btn-primary w-full">
            {t("booking.noShow.sheet.done")}
          </button>
        </div>
      </Sheet>
    </>
  );
}
